"use client";

import { useMemo } from "react";
import Link from "next/link";
import { useAuth } from "@clerk/nextjs";
import { useQuery } from "@tanstack/react-query";
import { apiFetchAuthed } from "../../lib/api";
import { queryKeys } from "../../lib/query-keys";

type OnboardingStatus = {
    hasUserProfile: boolean;
    hasHostProfile: boolean;
};

export function HostCTA() {
    const { isSignedIn, getToken } = useAuth();

    const { data: onboardingStatus } = useQuery({
        queryKey: queryKeys.onboarding.status(),
        queryFn: () => apiFetchAuthed<OnboardingStatus>("onboarding/status", {}, () => getToken()),
        enabled: isSignedIn === true,
        staleTime: 60_000,
    });

    const isHost = isSignedIn ? Boolean(onboardingStatus?.hasHostProfile) : null;
    const ctaHref = useMemo(() => {
        if (!isSignedIn) {
            // URL encode the redirect path properly
            return `/auth?redirect=${encodeURIComponent("/onboarding?start=hostType")}`;
        }
        if (isHost === false) {
            // Already signed in as guest, go directly to onboarding
            return "/onboarding?start=hostType";
        }
        // Already a host, go to dashboard
        return "/dashboard";
    }, [isSignedIn, isHost]);

    const ctaLabel = useMemo(() => {
        if (!isSignedIn) return "Start Hosting";
        if (isHost === false) return "Become a Host";
        return "Go to Dashboard";
    }, [isSignedIn, isHost]);

    return (
        <section className="relative px-4 py-16 sm:px-6 sm:py-24 md:px-12 md:py-32 lg:px-24">
            {/* Background gradient */}
            <div
                className="pointer-events-none absolute inset-0 opacity-20"
                style={{
                    background:
                        "radial-gradient(ellipse 80% 50% at 50% 100%, rgba(58,92,255, 0.15), transparent)",
                }}
            />

            <div className="relative mx-auto max-w-3xl text-center">
                <div className="mb-8 flex flex-col items-center gap-3">
                    <div className="flex flex-wrap items-center justify-center gap-3">
                        <div className="flex h-9 items-center gap-2 rounded-full border border-[var(--hopp-panel-strong)] bg-[var(--hopp-panel)] px-3 text-[10px] font-semibold text-[var(--hopp-text)]/80">
                            <span className="text-[11px] font-bold tracking-[0.2em]">VISA</span>
                        </div>
                        <div className="flex h-9 items-center gap-2 rounded-full border border-[var(--hopp-panel-strong)] bg-[var(--hopp-panel)] px-3 text-[10px] font-semibold text-[var(--hopp-text)]/80">
                            <svg viewBox="0 0 36 16" className="h-4 w-9" aria-hidden="true">
                                <circle cx="12" cy="8" r="6" fill="#EB001B" />
                                <circle cx="20" cy="8" r="6" fill="#F79E1B" fillOpacity="0.9" />
                            </svg>
                            Mastercard
                        </div>
                        <div className="flex h-9 items-center gap-2 rounded-full border border-[var(--hopp-panel-strong)] bg-[var(--hopp-panel)] px-3 text-[10px] font-semibold text-[var(--hopp-text)]/80">
                            <svg
                                viewBox="0 0 24 24"
                                className="h-4 w-4"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.6"
                                aria-hidden="true"
                            >
                                <path d="M3 10h18" />
                                <path d="M5 10V7l7-3 7 3v3" />
                                <path d="M5 10v8M9 10v8M15 10v8M19 10v8" />
                                <path d="M3 18h18" />
                            </svg>
                            Bank
                        </div>
                    </div>
                </div>
                {/* Heading */}
                <p className="text-[11px] uppercase tracking-[0.2em] text-[var(--hopp-muted)]">
                    For Curators
                </p>

                <h2 className="font-heading mt-4 text-[clamp(1.5rem,5vw,3rem)] font-bold text-[var(--hopp-text)]">
                    Host on HOPP
                </h2>

                <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-[var(--hopp-muted)] md:text-base">
                    Create events that matter. Reach the people who show up.
                </p>

                {/* CTA Button */}
                <div className="mt-10">
                    <Link href={ctaHref} className="group">
                        <span
                            className="inline-flex items-center gap-3 rounded-full border border-white/20 bg-white/10 px-8 py-4 text-sm font-semibold text-[#EDEDED] backdrop-blur-md shadow-[inset_0_1px_1px_rgba(255,255,255,0.1)] transition-all duration-150 hover:bg-white/15 hover:border-white/30 hover:scale-[1.02] active:scale-[0.98]"
                        >
                            {ctaLabel}
                            <span className="transition-transform duration-150 group-hover:translate-x-1">
                                →
                            </span>
                        </span>
                    </Link>
                </div>
            </div>
        </section>
    );
}
